import { AppText } from '../../components/AppText';
import { AppTextField } from '../../components/AppTextField';
import { InlineNotice } from '../../components/InlineNotice';
import { PrimaryButton } from '../../components/PrimaryButton';
import { ProviderButtons } from '../../components/ProviderButtons';
import { Strings } from '../../strings';

import { canSubmit, passwordsMatch } from './RegisterState';
import { useRegister } from './useRegister';

export function RegisterScreen({
  onRegistered,
  onHaveAccount,
}: {
  onRegistered: (email: string) => void;
  onHaveAccount: () => void;
}) {
  const model = useRegister(onRegistered);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void model.submit();
      }}
      style={{ display: 'flex', flexDirection: 'column', padding: '0 var(--space-6)' }}
    >
      <AppText as="h1" style="display">
        {Strings.actionCreateAccount}
      </AppText>

      <AppText
        color="var(--text-secondary)"
        sx={{ marginTop: 'var(--space-2)', marginBottom: 'var(--space-8)' }}
      >
        {Strings.registerSubtitle}
      </AppText>

      {model.state.error !== null ? (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <InlineNotice text={model.state.error} tone="error" />
        </div>
      ) : null}

      <AppTextField
        label={Strings.labelEmail}
        value={model.state.email}
        onChange={model.email}
        inputMode="email"
        type="email"
        autoComplete="email"
      />

      <div style={{ marginTop: 'var(--space-4)' }}>
        <AppTextField
          label={Strings.labelPassword}
          value={model.state.password}
          onChange={model.password}
          isPassword
          autoComplete="new-password"
        />
        {/*
          The rule stated before the parent submits, rather than only after the server rejects. The server
          stays the authority — it now names the configured minimum in its own message (R0074) — so this is
          a hint, not a second enforcement point.
        */}
        <div style={{ marginTop: 'var(--space-2)' }}>
          <AppText style="label" color="var(--text-secondary)">
            {Strings.registerPasswordHint}
          </AppText>
        </div>
      </div>

      <div style={{ marginTop: 'var(--space-4)' }}>
        <AppTextField
          label={Strings.labelPasswordConfirmation}
          value={model.state.passwordConfirmation}
          onChange={model.passwordConfirmation}
          isPassword
          autoComplete="new-password"
        />
      </div>

      {/*
        Shown only once there is something to compare, so it does not accuse the parent of a mismatch
        while they are still typing the second field.
      */}
      {model.state.passwordConfirmation.length > 0 && !passwordsMatch(model.state) ? (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <InlineNotice text={Strings.registerPasswordMismatch} tone="info" />
        </div>
      ) : null}

      <div style={{ marginTop: 'var(--space-4)' }}>
        <AppTextField
          label={Strings.labelDisplayName}
          value={model.state.displayName}
          onChange={model.displayName}
          autoComplete="name"
        />
      </div>

      <div style={{ marginTop: 'var(--space-8)' }}>
        <PrimaryButton
          type="submit"
          label={model.state.isSubmitting ? Strings.actionCreatingAccount : Strings.actionCreateAccount}
          enabled={canSubmit(model.state)}
          loading={model.state.isSubmitting}
        />
      </div>

      <ProviderButtons label={Strings.signInWithProvider} />

      <button
        type="button"
        onClick={onHaveAccount}
        style={{
          marginTop: 'var(--space-4)',
          background: 'none',
          border: 'none',
          padding: 0,
          textAlign: 'left',
          color: 'var(--action-primary)',
          fontSize: '15px',
          lineHeight: '20px',
          fontWeight: 600,
        }}
      >
        {Strings.actionHaveAccount}
      </button>
    </form>
  );
}
